import { useQuery } from '@tanstack/react-query';
import { TPO_GEN_DIM_OPTIONS_QUERY_KEY } from '../../../../utils/tpoGenQueryKeys';
import { tpoGenUrls } from 'config/urls';
import axios from 'axios';
import { type SelectOption } from '../../../../types/filters';
import { useMemo } from 'react';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import type { DimSelectFieldConfig } from '../../../../config/tpoGenConfig';

interface EventProduct {
    [key: string]: unknown;
}

interface EventProductsResponse {
    event_id: string;
    products: EventProduct[];
    total_count: number;
}

const fetchEventProducts = async (eventId: string, signal?: AbortSignal): Promise<EventProductsResponse> => {
    const token = localStorage.getItem('accessToken');

    const response = await axios(tpoGenUrls.getEventProducts(eventId), {
        method: 'GET',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
    });
    return response?.data;
};

const useProductAttributeFilters = ({ eventId }: { eventId: string }) => {
    const { promoDetailsFields } = useTpoGenConfig();
    const { data, isPending, isFetching, isError } = useQuery({
        queryKey: [TPO_GEN_DIM_OPTIONS_QUERY_KEY, 'product', eventId],
        queryFn: ({ signal }) => fetchEventProducts(eventId, signal),
        enabled: !!eventId,
    });

    const { ppgIdKey, ppgNameKey } = useMemo(() => {
        const lowestField = promoDetailsFields.find(
            (f): f is DimSelectFieldConfig => f.type === 'dimSelect' && (f as DimSelectFieldConfig).lowestAttribute,
        );
        const dim = lowestField?.dimension ?? 'dim1';
        return { ppgIdKey: `${dim}_id`, ppgNameKey: `${dim}_name` };
    }, [promoDetailsFields]);

    const options = useMemo<SelectOption[]>(() => {
        if (!data?.products) return [];
        return data.products.map((p) => ({
            label: (p[ppgNameKey] as string) || String(p[ppgIdKey]),
            value: String(p[ppgIdKey]),
        }));
    }, [data, ppgIdKey, ppgNameKey]);

    return {
        options,
        isPending,
        isFetching,
        isError,
    };
};

export default useProductAttributeFilters;
